import { pendingLaunchStages, type WorktreeLaunchProgress } from "./worktreeLaunchProgress";

export interface AdmissionWait {
  readonly startedAt: string;
  readonly message: string | null;
}

/** Reports current agent messages at or after admission start, ignoring old history and invalid dates. */
export function hasAgentWorkSince(
  messages: ReadonlyArray<{ readonly role: string; readonly createdAt: string }>,
  startedAt: string,
): boolean {
  const admissionTime = Date.parse(startedAt);
  if (!Number.isFinite(admissionTime)) return false;
  return messages.some((message) => {
    if (message.role !== "assistant" && message.role !== "reasoning") return false;
    const createdAt = Date.parse(message.createdAt);
    return Number.isFinite(createdAt) && createdAt >= admissionTime;
  });
}

/**
 * A synthetic WorktreeLaunchProgress for the window before any durable
 * worktree.* activity exists yet — most commonly a shared runtime busy with
 * another turn (a background agent or teammate). Renders through the same
 * launch card as real progress instead of a separate ad hoc one.
 *
 * Never reads activities: deriveWorktreeLaunchProgress stays the only real
 * source of truth, and WorktreeLaunchPanel drops this the moment a real
 * activity stream exists.
 */
export function buildAdmissionLaunchProgress(admission: AdmissionWait): WorktreeLaunchProgress {
  return {
    phase: "running",
    startedAt: admission.startedAt,
    endedAt: null,
    branch: null,
    baseBranch: null,
    worktreePath: null,
    terminalId: null,
    output: [],
    error: null,
    note: admission.message,
    stages: [
      {
        id: "admit",
        label: "Reserve environment",
        status: "running",
        startedAt: admission.startedAt,
        endedAt: null,
        percent: null,
        detail: null,
      },
      ...pendingLaunchStages(),
    ],
  };
}

/** Ends synthetic preparation when a real turn starts, marking later stages as unreached. */
export function settleAdmissionLaunchProgress(
  admission: AdmissionWait,
  settledAt: string,
): WorktreeLaunchProgress {
  const progress = buildAdmissionLaunchProgress(admission);
  return {
    ...progress,
    phase: "done",
    endedAt: settledAt,
    stages: progress.stages.map((stage, index) =>
      Object.assign({}, stage, {
        status: index === 0 ? "done" : "skipped",
        startedAt: stage.startedAt ?? admission.startedAt,
        endedAt: settledAt,
        detail: index === 0 ? "Environment ready" : "Not reached",
      }),
    ),
  };
}
